import { useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap'
import colors from 'Theme/colors'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { useBulkAutoPopulate } from './useBulkAutoPopulate'

// "Auto Populate Semua": fills every material following the schema rule, asking first when stock must be split.
// Returns the trigger, a busy flag and the split-confirmation modal to render.
export const useAutoPopulateAll = ({ rule, quantity, recipes, onApply }) => {
  const { calculateBulkAutoPopulate, executeBulkSplits, isProcessing } = useBulkAutoPopulate()
  const [pending, setPending] = useState(null)

  const run = async () => {
    const result = await calculateBulkAutoPopulate({ totalQty: quantity }, recipes, rule)
    if (!result) return
    if (result.requiresConfirmation) setPending(result)
    else onApply(result.pendingRecipeGroups)
  }

  const runForRecipe = async (targetRecipe, options = {}) => {
    const result = await calculateBulkAutoPopulate(
      { totalQty: quantity },
      [targetRecipe],
      rule,
      options,
    )
    if (!result) return
    if (result.requiresConfirmation) setPending(result)
    else onApply(result.pendingRecipeGroups)
  }

  const confirmSplit = async () => {
    const finalGroups = await executeBulkSplits(pending.pendingSplits, pending.pendingRecipeGroups)
    if (finalGroups) {
      onApply(finalGroups)
      setPending(null)
    }
  }

  const close = () => {
    if (!isProcessing) setPending(null)
  }

  const confirmModal = (
    <Modal isOpen={Boolean(pending)} toggle={close} centered>
      <ModalHeader toggle={close}>Konfirmasi Pemisahan Stok</ModalHeader>
      <ModalBody>
        <div className="mb-3" style={{ fontSize: '14px' }}>
          Stok berikut lebih besar dari kebutuhan. Sistem akan memecahnya: sebagian dipakai untuk pemetaan, sisanya
          tetap tersimpan di inventori.
        </div>
        <div className="d-flex flex-column gap-2 mb-3">
          {(pending?.pendingSplits || []).map((split, idx) => {
            const inv = split.targetInv || {}
            const unit = inv.unit || split.recipe?.unit || 'Kg'
            const total = Number(inv.amount ?? inv.amountNum ?? split.splitAmount + split.remainderAmount)
            const usedPct = total > 0 ? Math.min(100, (split.splitAmount / total) * 100) : 0
            const warehouse = inv.warehouseName || inv.customLabel?.warehouseName
            return (
              <div key={idx} className="p-3 border rounded-3 d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div>
                    <div className="fw-bold text-dark">{split.recipe?.nameItem || '-'}</div>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      {warehouse ? `Gudang ${warehouse} · ` : ''}Stok saat ini {newFormatThousandSeparator(total, 3)}{' '}
                      {unit}
                    </div>
                  </div>
                  <span
                    className="badge rounded-pill fw-semibold"
                    style={{ background: colors.primary[25], color: colors.primary[400], fontSize: '12px' }}
                  >
                    Lot ID: {inv.lotCode || '-'}
                  </span>
                </div>
                <div className="rounded-pill overflow-hidden d-flex" style={{ height: 8, background: colors.neutral[200] }}>
                  <div style={{ width: `${usedPct}%`, background: colors.primary[400] }} />
                </div>
                <div className="d-flex justify-content-between" style={{ fontSize: '13px' }}>
                  <span>
                    <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: colors.primary[400] }} />
                    Dipakai untuk pemetaan{' '}
                    <strong>
                      {newFormatThousandSeparator(split.splitAmount, 3)} {unit}
                    </strong>
                  </span>
                  <span className="text-muted">
                    <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: colors.neutral[300] }} />
                    Tetap di stok{' '}
                    <strong>
                      {newFormatThousandSeparator(split.remainderAmount, 3)} {unit}
                    </strong>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
        <div className="text-muted small">
          Lanjutkan pemecahan stok? Jika dibatalkan, material tidak akan diisi otomatis.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close} disabled={isProcessing}>
          Batal
        </Button>
        <Button color="primary" onClick={confirmSplit} disabled={isProcessing}>
          {isProcessing ? <Spinner size="sm" /> : 'Setuju & Lanjutkan'}
        </Button>
      </ModalFooter>
    </Modal>
  )

  return { run, runForRecipe, isProcessing, confirmModal }
}
